import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle, ArrowRight, Ban, Clock, Container, Info, Send, Truck, Warehouse,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { StockDashboard } from '../../types/stock.types';
import { stockDashboardApi } from '../../utils/stock-api';
import { cn } from '../../components/ui/utils';

/** Order-type colours, using the design system's chart tokens in order (§3). */
const TYPE_COLOUR: Record<string, string> = {
    delivery: 'var(--chart-1)',
    return: 'var(--chart-2)',
    import: 'var(--chart-3)',
    transfer: 'var(--chart-4)',
};

const TYPE_LABEL: Record<string, string> = {
    delivery: 'Deliveries',
    return: 'Returns',
    import: 'Import Orders',
    transfer: 'Transfers',
};

/**
 * Stock Dashboard — STOCK.md §3.
 *
 * Everything is scoped to the yards this user can see; the server does that filtering,
 * so nothing here re-derives access.
 *
 * Two of §3's cards — stock on hand, and on hire by project — are reported as
 * unavailable with the reason rather than rendered as zero. Idarah has no stock ledger
 * (§2.7) and Deliveries are not built (§4), so those numbers cannot be computed
 * truthfully, and a zero would read as "nothing out there".
 */
export default function StockDashboardPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_DASHBOARD);

    const [data, setData] = useState<StockDashboard | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setData(await stockDashboardApi.get());
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    if (!canRead) return <AccessRestricted />;

    if (loading || !data) {
        return (
            <div className="space-y-4 animate-pulse max-w-5xl mx-auto px-4 sm:px-6 py-8">
                {[0, 1, 2].map((i) => <div key={i} className="h-32 bg-gray-300 rounded-lg" />)}
            </div>
        );
    }

    const { workQueues, inTransit, exceptions, recentActivity, scope } = data;

    const exceptionCount =
        exceptions.overdueReturns.length +
        exceptions.stalledTransfers.length +
        exceptions.stalledReadyToEnter.length +
        exceptions.recentVoidsWithData.length;

    const queueTile = (
        label: string, value: number, href: string, icon: React.ReactNode, hint?: string
    ) => (
        <Link to={href} className="block">
            <div className="bg-card rounded-lg border border-border shadow-sm p-6 hover:border-slate-300 transition-colors h-full">
                <div className="flex items-start justify-between gap-2">
                    <span className="text-sm text-muted-foreground">{label}</span>
                    <span className="text-muted-foreground">{icon}</span>
                </div>
                <p className={cn('text-3xl font-semibold mt-2', value === 0 && 'text-muted-foreground')}>
                    {value}
                </p>
                {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
            </div>
        </Link>
    );

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Stock</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {scope.allYards
                        ? `All yards (${scope.yardCount}).`
                        : scope.yardCount === 0
                            ? 'You are not a member of any yard yet.'
                            : `Your yards: ${scope.yards.map((y) => y.yardCode).join(', ')}.`}
                </p>
            </div>

            {scope.yardCount === 0 && (
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <Warehouse size={28} className="mx-auto text-muted-foreground mb-3" />
                        <p className="font-medium">No yards visible</p>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                            Ask an administrator to add you as a member of a yard. Everything in Stock
                            is scoped to the yards you belong to.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Work queues */}
            <div>
                <h2 className="font-semibold mb-3">Work queues</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {queueTile(
                        'Returns awaiting pickup', workQueues.openUnassignedReturns,
                        '/stock/returns', <Truck size={18} />, 'Open and unassigned'
                    )}
                    {queueTile(
                        'Ready to Enter', workQueues.readyToEnter,
                        '/stock/returns', <Send size={18} />, 'Across all order types'
                    )}
                    {queueTile(
                        'Receipts in progress', workQueues.importReceiptsInProgress,
                        '/stock/imports', <Container size={18} />, 'Counted but not signed off'
                    )}
                </div>

                {/* §16.1.2 — one queue across all four types, broken down by type. */}
                {workQueues.readyToEnterByType.length > 0 && (
                    <div className="bg-card rounded-lg border border-border shadow-sm p-6 mt-4">
                        <p className="text-sm font-medium mb-3">Ready to Enter, by type</p>
                        <div className="space-y-2">
                            {workQueues.readyToEnterByType.map((t) => {
                                const pct = workQueues.readyToEnter > 0
                                    ? Math.round((t.count / workQueues.readyToEnter) * 100) : 0;
                                return (
                                    <div key={t.orderType} className="flex items-center gap-3">
                                        <span className="text-sm w-28 shrink-0">
                                            {TYPE_LABEL[t.orderType] ?? t.orderType}
                                        </span>
                                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full"
                                                style={{
                                                    width: `${pct}%`,
                                                    backgroundColor: TYPE_COLOUR[t.orderType] ?? 'var(--chart-5)',
                                                }}
                                            />
                                        </div>
                                        <span className="text-sm tabular-nums w-8 text-right">{t.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* In transit — §16.2.6 */}
            <div>
                <h2 className="font-semibold mb-3">In transit</h2>
                <div className="bg-card rounded-lg border border-border shadow-sm p-6">
                    {inTransit.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            Nothing between yards right now.
                        </p>
                    ) : (
                        <>
                            <p className="text-sm text-muted-foreground mb-4">
                                This material has left one yard and not arrived at the other. It is on
                                nobody's stock on hand until received.
                            </p>
                            <div className="space-y-2">
                                {inTransit.map((t) => (
                                    <Link
                                        key={t.id}
                                        to={`/stock/transfers/${t.id}`}
                                        className="flex flex-wrap items-center gap-2 text-sm py-2 border-b border-border last:border-0 hover:text-foreground"
                                    >
                                        <span className="font-mono">{t.orderNumber}</span>
                                        <span className="flex items-center gap-1.5 text-muted-foreground">
                                            {t.originYard}<ArrowRight size={12} />{t.destinationYard}
                                        </span>
                                        <span className={cn(
                                            'ml-auto flex items-center gap-1 text-xs',
                                            t.daysElapsed >= exceptions.thresholds.transferTransitDays
                                                ? 'text-amber-700 font-medium' : 'text-muted-foreground'
                                        )}>
                                            <Clock size={12} />{t.daysElapsed}d
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Exceptions */}
            <div>
                <h2 className="font-semibold mb-3 flex items-center gap-2">
                    Exceptions
                    {exceptionCount > 0 && (
                        <span className="text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-2 py-0.5 rounded-md">
                            {exceptionCount}
                        </span>
                    )}
                </h2>
                <div className="bg-card rounded-lg border border-border shadow-sm p-6 space-y-5">
                    {exceptionCount === 0 ? (
                        <p className="text-sm text-muted-foreground">Nothing needs attention.</p>
                    ) : (
                        <>
                            {exceptions.overdueReturns.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium flex items-center gap-1.5 mb-2">
                                        <AlertTriangle size={14} className="text-amber-600" />
                                        Returns open more than {exceptions.thresholds.returnOpenDays} days
                                    </p>
                                    {exceptions.overdueReturns.map((r) => (
                                        <Link key={r.id} to={`/stock/returns/${r.id}`}
                                              className="flex justify-between text-sm py-1 hover:underline">
                                            <span className="font-mono">{r.orderNumber}</span>
                                            <span className="text-muted-foreground">{r.daysOpen}d</span>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {exceptions.stalledTransfers.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium flex items-center gap-1.5 mb-2">
                                        <AlertTriangle size={14} className="text-amber-600" />
                                        Transfers in transit more than {exceptions.thresholds.transferTransitDays} days
                                    </p>
                                    {exceptions.stalledTransfers.map((t) => (
                                        <Link key={t.id} to={`/stock/transfers/${t.id}`}
                                              className="flex justify-between text-sm py-1 hover:underline">
                                            <span className="font-mono">{t.orderNumber}</span>
                                            <span className="text-muted-foreground">{t.daysElapsed}d</span>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {exceptions.stalledReadyToEnter.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium flex items-center gap-1.5 mb-2">
                                        <AlertTriangle size={14} className="text-amber-600" />
                                        Waiting to be entered more than {exceptions.thresholds.readyToEnterDays} days
                                    </p>
                                    {exceptions.stalledReadyToEnter.map((r) => (
                                        <div key={r.id} className="flex justify-between text-sm py-1">
                                            <span className="font-mono">{r.orderNumber}</span>
                                            <span className="text-muted-foreground">{r.daysWaiting}d</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {exceptions.recentVoidsWithData.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium flex items-center gap-1.5 mb-2">
                                        <Ban size={14} className="text-red-600" />
                                        Voided with material on them, last 30 days
                                    </p>
                                    <p className="text-xs text-muted-foreground mb-2">
                                        A void that carried counted material is the one worth reviewing —
                                        the full record survives in the audit trail.
                                    </p>
                                    {exceptions.recentVoidsWithData.map((v) => (
                                        <div key={v.id} className="text-sm py-1">
                                            <div className="flex justify-between">
                                                <span className="font-mono">{v.orderNumber}</span>
                                                <span className="text-muted-foreground capitalize">
                                                    {v.voidReasonCode?.replace(/_/g, ' ') ?? '–'} · {v.lineCount} line(s)
                                                </span>
                                            </div>
                                            {v.voidReason && (
                                                <p className="text-xs text-muted-foreground">{v.voidReason}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Recent activity */}
            <div>
                <h2 className="font-semibold mb-3">Recent activity</h2>
                <div className="bg-card rounded-lg border border-border shadow-sm p-6">
                    {recentActivity.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No activity yet.</p>
                    ) : (
                        <div className="space-y-2">
                            {recentActivity.map((a) => (
                                <div key={a.id} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                                    <span className="text-xs text-muted-foreground tabular-nums">
                                        {new Date(a.createdAt).toLocaleString()}
                                    </span>
                                    <span className="font-mono">{a.orderNumber}</span>
                                    <span className="text-muted-foreground">
                                        {a.fromStatus
                                            ? `${a.fromStatus.replace(/_/g, ' ')} → ${a.toStatus.replace(/_/g, ' ')}`
                                            : `created as ${a.toStatus.replace(/_/g, ' ')}`}
                                    </span>
                                    <span className="text-muted-foreground">· {a.actorName}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* What this dashboard deliberately cannot tell you */}
            {data.unavailable.length > 0 && (
                <div className="bg-muted/40 rounded-lg border border-border p-6">
                    <p className="text-sm font-medium flex items-center gap-1.5 mb-3">
                        <Info size={14} className="text-muted-foreground" />
                        Not shown yet
                    </p>
                    <div className="space-y-3">
                        {data.unavailable.map((u) => (
                            <div key={u.card}>
                                <p className="text-sm font-medium">{u.card}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{u.reason}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
